/**
 * One payout, fully itemised.
 *
 * WHY the per-line fee breakdown is collapsed by default: a payout has hundreds of lines and
 * each line has several components, so expanding everything makes the page unreadable. The
 * headline numbers (declared, computed, delta) and the findings are what must be visible
 * without a click.
 */
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type Line } from "../api";
import { Amount } from "../components/Amount";
import { ResultBadge, StatusBadge } from "../components/Badges";
import { DiscrepancyList } from "../components/DiscrepancyList";
import { RetainedFee } from "../components/RetainedFee";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatCount } from "../money";
import { formatDateTime, shortExternalId } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

export function PayoutDetail(): ReactNode {
  const store = useStore();
  const { batchId = "" } = useParams<{ batchId: string }>();
  const state = useAsync(() => api.payout(store.id, batchId), [store.id, batchId]);

  if (state.status === "loading") return <Loading label="Loading payout" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;
  const { batch, lines, discrepancies } = state.data;

  return (
    <>
      <header className="page-head">
        <div>
          <p className="crumb"><Link to={`/stores/${store.id}/payouts`}>Payouts</Link></p>
          <h1 className="page-title mono" title={batch.external_id}>{shortExternalId(batch.external_id, 32)}</h1>
          <p className="page-sub">
            {batch.kind} · {batch.direction} · issued {formatDateTime(batch.issued_at)} · <StatusBadge status={batch.status} />
          </p>
        </div>
        <ResultBadge result={batch.result} />
      </header>

      <div className="tiles tiles-3">
        <section className="tile"><h2 className="tile-label">Declared net</h2><div className="tile-body"><Amount minor={batch.declared_net_minor} currency={batch.currency} strong /></div><p className="tile-hint">What the platform says it paid.</p></section>
        <section className="tile"><h2 className="tile-label">Computed net</h2><div className="tile-body"><Amount minor={batch.computed_net_minor} currency={batch.currency} strong /></div><p className="tile-hint">Σ of the {formatCount(batch.line_count)} settlement lines below.</p></section>
        <section className={`tile ${batch.delta_minor && batch.delta_minor !== "0" ? "tile-bad" : ""}`}><h2 className="tile-label">Delta</h2><div className="tile-body"><Amount minor={batch.delta_minor} currency={batch.currency} signed strong /></div><p className="tile-hint">R1: a reconciled payout has a delta of zero.</p></section>
      </div>

      <section className="card">
        <h2 className="card-title">Disbursement</h2>
        {batch.disbursement ? (
          <dl className="kv">
            <div><dt>Reference</dt><dd className="mono" title={batch.disbursement.external_id}>{shortExternalId(batch.disbursement.external_id, 32)}</dd></div>
            <div><dt>Status</dt><dd><StatusBadge status={batch.disbursement.status} /></dd></div>
            <div><dt>Amount</dt><dd><Amount minor={batch.disbursement.amount_minor} currency={batch.disbursement.currency} /></dd></div>
            <div><dt>Bank trace</dt><dd className="mono">{batch.disbursement.bank_trace ?? "—"}</dd></div>
            <div><dt>Paid at</dt><dd>{formatDateTime(batch.disbursement.paid_at)}</dd></div>
          </dl>
        ) : (
          <p className="state-hint">No bank transfer is linked to this payout yet. The batch is the platform's grouping; the disbursement is the money leaving it.</p>
        )}
      </section>

      <section className="card">
        <h2 className="card-title">Lines <span className="count-chip">{formatCount(batch.line_count)}</span></h2>
        {lines.length === 0 ? (
          <EmptyState title="No lines on this payout" hint="The payout exists but its transactions have not been synced yet." />
        ) : (
          <LinesTable lines={lines} />
        )}
      </section>

      <section className="card">
        <h2 className="card-title">Discrepancies</h2>
        {discrepancies.length === 0 ? (
          <p className="state-hint">No open findings on this payout.</p>
        ) : (
          <DiscrepancyList items={discrepancies} storeId={store.id} />
        )}
      </section>
    </>
  );
}

export function LinesTable({ lines }: { lines: Line[] }): ReactNode {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th aria-label="Expand" /><th>Occurred</th><th>Kind</th><th>Native type</th><th>Order</th>
            <th className="num">Gross</th><th className="num">Fee</th><th className="num">Net</th><th>Settlement</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const open = expanded.has(line.id);
            return [
              <tr key={line.id}>
                <td>
                  <button
                    type="button"
                    className="disclosure"
                    aria-expanded={open}
                    onClick={() => toggle(line.id)}
                    title={line.components.length ? "Fee breakdown" : "No components recorded"}
                    disabled={line.components.length === 0}
                  >
                    {open ? "−" : "+"}
                  </button>
                </td>
                <td>{formatDateTime(line.occurred_at)}</td>
                <td>{line.line_kind}{line.is_test ? <span className="chip">TEST</span> : null}</td>
                <td className="mono">{line.native_type}{line.native_subtype && line.native_subtype !== line.native_type ? ` / ${line.native_subtype}` : ""}</td>
                <td>{line.order_number ?? <span className="muted" title={line.order_external_id ?? "no order reference"}>—</span>}</td>
                <td className="num"><Amount minor={line.gross_minor} currency={line.currency} /></td>
                <td className="num" title="Fee in seller perspective: negative was deducted, positive was returned — expand the row for the breakdown">
                  <Amount minor={line.fee_minor} currency={line.currency} signed />
                  <RetainedFee line={line} />
                </td>
                <td className="num"><Amount minor={line.net_minor} currency={line.currency} /></td>
                <td><StatusBadge status={line.settlement_status} /></td>
              </tr>,
              open ? (
                <tr key={`${line.id}-components`} className="subrow">
                  <td />
                  <td colSpan={8}>
                    <table className="table table-inner">
                      <thead><tr><th>Group</th><th>Native name</th><th>Fee code</th><th className="num">Amount</th></tr></thead>
                      <tbody>
                        {line.components.map((c, i) => (
                          <tr key={`${c.group}-${c.native_name}-${i}`}>
                            <td>{c.group}</td>
                            <td className="mono">{c.native_name}</td>
                            <td>{c.fee_code ?? <span className="muted">—</span>}</td>
                            <td className="num"><Amount minor={c.amount_minor} currency={c.currency} signed /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {line.adjustment_class ? (
                      <p className="hint">Adjustment class: <strong>{line.adjustment_class}</strong>{line.adjustment_reason_text ? ` — ${line.adjustment_reason_text}` : ""}</p>
                    ) : null}
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}
